import { type ReactNode, type ElementType } from 'react'
import { cn } from '@/lib/utils'

interface EmptyStateProps {
  /** Large icon rendered in the circle — pass a Lucide component */
  icon?: ElementType
  title: string
  hint?: string
  action?: ReactNode
  className?: string
}

/**
 * Rich empty / no-results state — shadcn empty aesthetic.
 *
 * Shows an icon in a subtle circle, a display-font title, muted hint, and an optional CTA.
 * Used for filtered-empty lists, 404 error states, and any "nothing here yet" screen.
 */
export function EmptyState({ icon: Icon, title, hint, action, className }: EmptyStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center py-20 text-center px-6', className)}>
      {/* Icon circle */}
      {Icon && (
        <div className="mb-6 flex items-center justify-center w-16 h-16 rounded-full bg-sand-200 dark:bg-slate-800 border border-sand-400 dark:border-slate-700">
          <Icon className="w-7 h-7 text-slate-400 dark:text-slate-500" strokeWidth={1.5} />
        </div>
      )}

      {/* Title */}
      <h2 className="font-display text-lg font-semibold text-slate-800 dark:text-sand-100">
        {title}
      </h2>

      {/* Hint */}
      {hint && (
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400 max-w-sm leading-relaxed font-sans">
          {hint}
        </p>
      )}

      {/* Action */}
      {action && <div className="mt-6">{action}</div>}
    </div>
  )
}
